// Utility functions for translation management

// Force reset Google Translate to English by reloading the page with clean state
export const forceResetToEnglish = (): void => {
  console.log("🔄 Force resetting to English with page reload...");
  console.log("📍 Current URL:", window.location.href);
  console.log("🍪 Current cookies before clear:", document.cookie);

  // Clear all Google Translate cookies more aggressively
  const cookies = [
    "googtrans",
    "googtrans=/en/en",
    "googtrans=/en/si",
    "googtrans=/en/ta",
    "googtrans=/si/en",
    "googtrans=/ta/en",
    "googtrans=/si/si",
    "googtrans=/ta/ta",
  ];

  // Also clear any existing googtrans cookie
  const existingCookies = document.cookie.split(";");
  existingCookies.forEach((cookie) => {
    const [name] = cookie.trim().split("=");
    if (name.startsWith("googtrans")) {
      cookies.push(name);
    }
  });

  // Clear cookies with multiple domain variations
  cookies.forEach((cookie) => {
    const domains = [
      window.location.hostname,
      `.${window.location.hostname}`,
      window.location.hostname.split(".").slice(-2).join("."),
      `.${window.location.hostname.split(".").slice(-2).join(".")}`,
    ];

    domains.forEach((domain) => {
      document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure;`;
      document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax;`;
    });
  });

  // Set English as the default language
  document.cookie =
    "googtrans=/en/en; Path=/; Domain=" + window.location.hostname;
  document.cookie =
    "googtrans=/en/en; Path=/; Domain=." + window.location.hostname;
  document.cookie = "googtrans=/en/en; Path=/";

  // Update localStorage
  localStorage.setItem("mealbridge-language", "en");

  // Clear any existing translation state
  const body = document.body;
  if (body) {
    body.classList.remove("translated-ltr", "translated-rtl");
    body.style.direction = "ltr";
  }

  // Hide all Google Translate elements
  const googleElements = document.querySelectorAll('[class*="goog-te"]');
  googleElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.display = "none";
    }
  });

  console.log("🍪 Cookies after clear:", document.cookie);
  console.log("🔄 Reloading page to force English state...");

  // Reload the page to ensure clean English state
  window.location.reload();
};

export const resetGoogleTranslateToEnglish = (): Promise<boolean> => {
  return new Promise((resolve) => {
    console.log("🔄 Resetting Google Translate to English...");
    console.log("📍 Current URL:", window.location.href);
    console.log("🍪 Current cookies:", document.cookie);

    // First, try a more direct approach by setting the googtrans cookie directly
    document.cookie =
      "googtrans=/en/en; Path=/; Domain=" + window.location.hostname;
    document.cookie =
      "googtrans=/en/en; Path=/; Domain=." + window.location.hostname;
    document.cookie = "googtrans=/en/en; Path=/";

    // Clear Google Translate cookies more aggressively
    const cookies = [
      "googtrans",
      "googtrans=/en/en",
      "googtrans=/en/si",
      "googtrans=/en/ta",
      "googtrans=/si/en",
      "googtrans=/ta/en",
      "googtrans=/si/si",
      "googtrans=/ta/ta",
    ];

    // Also clear any existing googtrans cookie
    const existingCookies = document.cookie.split(";");
    existingCookies.forEach((cookie) => {
      const [name] = cookie.trim().split("=");
      if (name.startsWith("googtrans")) {
        cookies.push(name);
      }
    });

    // Clear cookies with multiple domain variations
    cookies.forEach((cookie) => {
      const domains = [
        window.location.hostname,
        `.${window.location.hostname}`,
        window.location.hostname.split(".").slice(-2).join("."),
        `.${window.location.hostname.split(".").slice(-2).join(".")}`,
      ];

      domains.forEach((domain) => {
        document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure;`;
        document.cookie = `${cookie}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax;`;
      });
    });

    // Clear translation state
    const body = document.body;
    if (body) {
      body.classList.remove("translated-ltr", "translated-rtl");
      body.style.direction = "ltr";
    }

    // Remove all Google Translate elements and styling
    const googleElements = document.querySelectorAll('[class*="goog-te"]');
    googleElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.display = "none";
        el.remove();
      }
    });

    // Try to reset Google Translate combo with multiple attempts
    const resetCombo = () => {
      const combo = document.querySelector(
        ".goog-te-combo"
      ) as HTMLSelectElement;
      if (combo) {
        console.log("🔍 Found combo, resetting to English");
        console.log("🔍 Current combo value:", combo.value);
        console.log(
          "🔍 Available options:",
          Array.from(combo.options).map((opt) => ({
            value: opt.value,
            text: opt.text,
          }))
        );

        // Try different values that might represent English
        const englishValues = ["0", "", "en", "auto", "en-US", "en-GB"];

        // First, try to find the actual English option in the dropdown
        const englishOption = Array.from(combo.options).find(
          (option) =>
            option.text.toLowerCase().includes("english") ||
            option.value === "0" ||
            option.value === "en" ||
            option.value === ""
        );

        if (englishOption) {
          console.log(
            "✅ Found English option:",
            englishOption.value,
            englishOption.text
          );
          combo.value = englishOption.value;
        } else {
          console.log("⚠️ English option not found, trying common values");
          // Fallback to trying all possible values
          for (const value of englishValues) {
            if (Array.from(combo.options).some((opt) => opt.value === value)) {
              combo.value = value;
              console.log("✅ Set combo to:", value);
              break;
            }
          }
        }

        // Force multiple events to ensure the change is processed
        combo.dispatchEvent(new Event("change", { bubbles: true }));
        combo.dispatchEvent(new Event("input", { bubbles: true }));
        combo.dispatchEvent(new Event("click", { bubbles: true }));

        // Force focus and blur to trigger processing
        combo.focus();
        combo.blur();
        combo.focus();
        combo.blur();

        // Try to force Google Translate to process the change
        setTimeout(() => {
          // Trigger a custom event that Google Translate might listen to
          const event = new CustomEvent("googtrans", {
            detail: { value: combo.value, language: "en" },
            bubbles: true,
          });
          document.dispatchEvent(event);
          combo.dispatchEvent(event);

          // Also try to trigger the Google Translate internal change
          if ((window as any).google && (window as any).google.translate) {
            try {
              // Force the translation to English
              const translateElement = document.getElementById(
                "google_translate_element"
              );
              if (translateElement && translateElement.querySelector) {
                const iframe = translateElement.querySelector("iframe");
                if (iframe && iframe.contentWindow) {
                  iframe.contentWindow.postMessage(
                    {
                      type: "googtrans",
                      value: combo.value,
                      action: "setLanguage",
                      language: "en",
                    },
                    "*"
                  );
                }
              }
            } catch (error) {
              console.log(
                "⚠️ Could not send message to Google Translate iframe:",
                error
              );
            }
          }

          // Try to force a page refresh of Google Translate
          if (
            (window as any).google &&
            (window as any).google.translate &&
            (window as any).google.translate.TranslateElement
          ) {
            try {
              // Force refresh the translation
              const translateElement = document.getElementById(
                "google_translate_element"
              );
              if (translateElement) {
                console.log("🔄 Recreating Google Translate element...");
                // Remove and recreate the translate element
                translateElement.innerHTML = "";

                // Recreate with English as default
                new (window as any).google.translate.TranslateElement(
                  {
                    pageLanguage: "en",
                    includedLanguages: "en,si,ta",
                    layout: (window as any).google.translate.TranslateElement
                      .InlineLayout.SIMPLE,
                    autoDisplay: false,
                    multilanguagePage: true,
                  },
                  "google_translate_element"
                );

                console.log(
                  "✅ Recreated Google Translate element for English"
                );
              }
            } catch (error) {
              console.log(
                "⚠️ Could not recreate Google Translate element:",
                error
              );
            }
          }
        }, 100);

        console.log("English reset attempted with value:", combo.value);
        return true;
      }
      return false;
    };

    // Try to reset immediately
    if (resetCombo()) {
      console.log("✅ Google Translate reset to English");
      resolve(true);
      return;
    }

    // If combo not found, wait a bit and try again with multiple attempts
    let attempts = 0;
    const maxAttempts = 5;

    const retryReset = () => {
      attempts++;
      console.log(
        `🔄 Retry attempt ${attempts}/${maxAttempts} for English reset`
      );

      if (resetCombo()) {
        console.log("✅ Google Translate reset to English (delayed)");
        resolve(true);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(retryReset, 1000);
      } else {
        console.log(
          "❌ Could not reset Google Translate combo after multiple attempts"
        );
        // Instead of failing, try to force a clean state without reload
        console.log("🔄 Attempting to force clean English state...");

        // Force remove all translation classes and styling
        const body = document.body;
        if (body) {
          body.classList.remove("translated-ltr", "translated-rtl");
          body.style.direction = "ltr";
        }

        // Hide all Google Translate elements
        const googleElements = document.querySelectorAll('[class*="goog-te"]');
        googleElements.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.display = "none";
          }
        });

        // Try to restore original text by removing Google Translate spans
        const translatedSpans = document.querySelectorAll(
          'span[style*="direction"]'
        );
        translatedSpans.forEach((span) => {
          if (span.parentNode && span.textContent) {
            const textNode = document.createTextNode(span.textContent);
            span.parentNode.replaceChild(textNode, span);
          }
        });

        // Remove any Google Translate iframes
        const iframes = document.querySelectorAll(
          'iframe[src*="translate.google"]'
        );
        iframes.forEach((iframe) => {
          iframe.remove();
        });

        // Force Google Translate to detect language change
        if ((window as any).google && (window as any).google.translate) {
          try {
            // Try to trigger Google Translate's internal language change
            const translateElement = document.getElementById(
              "google_translate_element"
            );
            if (translateElement) {
              console.log("🔄 Reinitializing Google Translate element...");
              // Force a refresh of the translate element
              translateElement.innerHTML = "";

              // Reinitialize Google Translate with English
              new (window as any).google.translate.TranslateElement(
                {
                  pageLanguage: "en",
                  includedLanguages: "en,si,ta",
                  layout: (window as any).google.translate.TranslateElement
                    .InlineLayout.SIMPLE,
                  autoDisplay: false,
                  multilanguagePage: true,
                },
                "google_translate_element"
              );
              console.log("✅ Google Translate element reinitialized");
            }
          } catch (error) {
            console.log("⚠️ Could not reinitialize Google Translate:", error);
          }
        }

        console.log("✅ Forced clean English state (no page reload)");
        resolve(true);
      }
    };

    setTimeout(retryReset, 1000);
  });
};

export const waitForGoogleTranslateCombo = (
  maxAttempts: number = 20
): Promise<HTMLSelectElement | null> => {
  return new Promise((resolve) => {
    let attempts = 0;

    const checkForCombo = () => {
      const combo = document.querySelector(
        ".goog-te-combo"
      ) as HTMLSelectElement;
      if (combo) {
        console.log("✅ Google Translate combo found");
        resolve(combo);
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        console.log(
          "❌ Google Translate combo not found after",
          maxAttempts,
          "attempts"
        );
        resolve(null);
        return;
      }

      setTimeout(checkForCombo, 200);
    };

    checkForCombo();
  });
};

export const setGoogleTranslateLanguage = (
  languageCode: string
): Promise<boolean> => {
  return new Promise(async (resolve) => {
    const combo = await waitForGoogleTranslateCombo();

    if (!combo) {
      console.log("❌ Could not find Google Translate combo");
      resolve(false);
      return;
    }

    console.log(`🔄 Setting Google Translate to: ${languageCode}`);

    // Set the language
    combo.value = languageCode;
    combo.dispatchEvent(new Event("change", { bubbles: true }));
    combo.dispatchEvent(new Event("input", { bubbles: true }));
    combo.dispatchEvent(new Event("click", { bubbles: true }));

    // Wait a bit for the translation to take effect
    setTimeout(() => {
      console.log(`✅ Google Translate set to: ${languageCode}`);
      resolve(true);
    }, 1000);
  });
};
